import {
  z,
  createValidationSchema,
  validateMobilePhone,
  ARABIC_LATIN_NAME_REGEX,
  createConditionalEmailSchema,
  validateEmailWithCompanyContext as validateEmailWithContext,
} from '@tasama/utils';

/**
 * User profile schemas for the portal app
 * Using zod and validation utilities from @tasama/utils
 */

// Function to get translated validation messages from i18n
export const getValidationMessages = (t: (_key: string) => string) => ({
  firstName: {
    required: t('errors.validation.firstName.required'),
    min: t('errors.validation.firstName.min'),
    max: t('errors.validation.firstName.max'),
    charset: t('errors.validation.firstName.charset'),
  },
  lastName: {
    required: t('errors.validation.lastName.required'),
    min: t('errors.validation.lastName.min'),
    max: t('errors.validation.lastName.max'),
    charset: t('errors.validation.lastName.charset'),
  },
  salutation: {
    required: t('errors.validation.salutation.required'),
  },
  jobTitle: {
    required: t('errors.validation.jobTitle.required'),
    max: t('errors.validation.jobTitle.max'),
  },
  email: {
    required: t('errors.validation.email.required'),
    invalid: t('errors.validation.email.invalid'),
    business: t('errors.validation.email.business'),
    pif: t('errors.validation.email.pif'),
  },
  country: {
    required: t('errors.validation.country.required'),
  },
  mobile: {
    required: t('errors.validation.mobile.required'),
    invalid: t('errors.validation.mobile.invalid'),
  },
});

const NAME_CHARSET_REGEX = ARABIC_LATIN_NAME_REGEX;

// Common validation helpers
const required = (message: string) => createValidationSchema.required(message);

// 3. Prefix Enum definition
export const NamePrefixEnum = z.enum(['Mr', 'Mrs', 'Ms', 'Miss', 'Dr']);

// 4. Function to create conditional email validation schema
export const createYourDetailsSchema = (
  companyType: string,
  companyStatus: string,
  t: (_key: string) => string
) => {
  const messages = getValidationMessages(t);

  // Use the centralized conditional email validation from api-client
  const emailOptions: {
    required: boolean;
    companyType?: string;
    companyStatus?: string;
    messages: {
      required: string;
      invalid: string;
      corporate: string;
      pif: string;
    };
  } = {
    required: true,
    messages: {
      required: messages.email.required,
      invalid: messages.email.invalid,
      corporate: messages.email.business,
      pif: messages.email.pif,
    },
  };

  if (companyType !== undefined) {
    emailOptions.companyType = companyType;
  }

  if (companyStatus !== undefined) {
    emailOptions.companyStatus = companyStatus;
  }

  const finalEmailValidation = createConditionalEmailSchema(
    emailOptions
  ).transform((val: string | undefined) => val?.trim() || '');

  return z
    .object({
      firstName: z
        .string()
        .trim()
        .min(1, messages.firstName.required)
        .min(2, messages.firstName.min)
        .max(250, messages.firstName.max)
        .regex(NAME_CHARSET_REGEX, messages.firstName.charset),
      lastName: z
        .string()
        .trim()
        .min(1, messages.lastName.required)
        .min(2, messages.lastName.min)
        .max(250, messages.lastName.max)
        .regex(NAME_CHARSET_REGEX, messages.lastName.charset),
      // Salutation (formerly Prefix) must be one of the allowed enum values
      salutation: z
        .string()
        .min(1, messages.salutation.required)
        .refine((value) => ['Mr', 'Mrs', 'Ms', 'Miss', 'Dr'].includes(value), {
          error: messages.salutation.required,
        }),
      jobTitle: z
        .string()
        .trim()
        .min(1, messages.jobTitle.required)
        .max(250, messages.jobTitle.max),
      email: finalEmailValidation,
      country: required(messages.country.required),
      mobile: z
        .string()
        .trim()
        .min(1, messages.mobile.required)
        .regex(/^[0-9]+$/, messages.mobile.invalid),
      // Computed combined phone number (country + mobile) - optional, maintained in store
      phoneNumber: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      const phone = (data.mobile ?? '').trim();
      const country = (data.country ?? '').trim();

      if (!phone) return;

      if (!validateMobilePhone(country, phone)) {
        ctx.addIssue({
          path: ['mobile'],
          code: 'custom',
          message: messages.mobile.invalid,
        });
      }
    });
};

// 5. Type exports
export type YourDetailsFormValues = z.infer<
  ReturnType<typeof createYourDetailsSchema>
>;

// 6. Helper function to validate email based on company context
export const validateEmailWithCompanyContext = (
  email: string,
  companyType?: string,
  companyStatus?: string
): string | null => {
  const result = validateEmailWithContext(email, companyType, companyStatus);
  return result.isValid ? null : result.error || null;
};

// 7. Interfaces for API/Application logic
export interface UserProfileDetails {
  firstName: string;
  lastName: string;
  salutation?: string;
  jobTitle: string;
  email: string;
  mobile: string;
  countryCode: string;
}

// 8. Default values for UI initial state
export const yourDetailsDefaultValues: Partial<YourDetailsFormValues> = {
  firstName: '',
  lastName: '',
  salutation: '' as any, // No default selection - user must choose (use empty string for exactOptionalPropertyTypes)
  jobTitle: '',
  email: '',
  country: '+966',
  mobile: '',
  phoneNumber: '',
};
